import type { ReactNode } from "react";
import { useCallback, useDeferredValue, useMemo, useState } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { Combobox, DropdownButton } from "@similarweb/ui-components";

import useDelayedAction from "app/pages/sales-intelligence/hooks/useDelayedAction";

import { ScrollDismissBoundary } from "../../scroll-dismiss/ScrollDismissBoundary";
import { StyledBaseDropdownButtonWrap } from "../../styles";
import type { IGenericDropDownItem } from "../types";
import { renderCheckboxOption } from "../utils/renderCheckboxOption";

import SelectAllOption from "./SelectAllOption";

const EMPTY_ITEMS: readonly never[] = [] as const;
const EMPTY_SELECTION: readonly never[] = [] as const;
const AUTO_OPEN_DELAY_MS = 300;

type ItemId = string | number;

type ComboboxWithSelectAllBaseProps<TId extends ItemId, TItem extends IGenericDropDownItem<TId>> = {
    items: readonly TItem[];
    selectAllLabel: string;
    hasSearch?: boolean;
    searchPlaceHolder?: string;
    disabled?: boolean;
    dataAutomation?: string;
    autoOpen?: boolean;
    isTopLayer?: boolean;
    width?: number;
    filterItem?: (item: TItem, searchTerm: string) => boolean;
    renderButton?: (props: { isOpen: boolean; disabled?: boolean }) => ReactNode;
    renderOption?: (item: TItem, isSelected: boolean) => ReactNode;
};

type ComboboxWithSelectAllBuiltInProps<
    TId extends ItemId,
    TItem extends IGenericDropDownItem<TId>,
> = ComboboxWithSelectAllBaseProps<TId, TItem> & {
    buttonText: string;
    selectedItems: readonly TId[] | undefined;
    onSelectionChange: (items: readonly TId[], isAllSelected: boolean) => void;
};

type ComboboxWithSelectAllCustomProps<
    TId extends ItemId,
    TItem extends IGenericDropDownItem<TId>,
> = ComboboxWithSelectAllBaseProps<TId, TItem> & {
    isFullSelection: boolean;
    isPartiallySelected: boolean;
    isItemSelected: (item: TItem) => boolean;
    onSelectAll: () => void;
    onSelect: (item: TItem) => void;
};

export type ComboboxWithSelectAllProps<TId extends ItemId, TItem extends IGenericDropDownItem<TId>> =
    | ComboboxWithSelectAllBuiltInProps<TId, TItem>
    | ComboboxWithSelectAllCustomProps<TId, TItem>;

const isCustomSelectionProps = <TId extends ItemId, TItem extends IGenericDropDownItem<TId>>(
    props: ComboboxWithSelectAllProps<TId, TItem>,
): props is ComboboxWithSelectAllCustomProps<TId, TItem> => "onSelect" in props;

/**
 * Multi-select combobox with optional "Select All". Built-in mode (`selectedItems` + `onSelectionChange`)
 * covers flat lists; custom mode (`isFullSelection`, `onSelectAll`, `onSelect`, `renderOption`) is for
 * consumers that own non-trivial selection semantics (e.g. signal filters where empty means "any").
 */
const ComboboxWithSelectAll = <TId extends ItemId, TItem extends IGenericDropDownItem<TId>>(
    props: ComboboxWithSelectAllProps<TId, TItem>,
) => {
    const {
        items = EMPTY_ITEMS,
        selectAllLabel,
        hasSearch = false,
        searchPlaceHolder,
        disabled = false,
        dataAutomation,
        autoOpen = false,
        isTopLayer = false,
        width,
        filterItem,
        renderButton,
        renderOption = renderCheckboxOption,
    } = props;

    const { translate } = useTranslate();
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const deferredSearchTerm = useDeferredValue(searchTerm);

    useDelayedAction(AUTO_OPEN_DELAY_MS, () => setIsOpen(true), autoOpen);

    const isCustomMode = isCustomSelectionProps(props);
    const customIsItemSelected = isCustomMode ? props.isItemSelected : undefined;
    const customOnSelectAll = isCustomMode ? props.onSelectAll : undefined;
    const customOnSelect = isCustomMode ? props.onSelect : undefined;
    const selectedItems = isCustomMode
        ? (EMPTY_SELECTION as readonly TId[])
        : (props.selectedItems ?? (EMPTY_SELECTION as readonly TId[]));
    const onSelectionChange = isCustomMode ? undefined : props.onSelectionChange;
    const buttonText = isCustomMode ? undefined : props.buttonText;

    if (disabled && isOpen) {
        setIsOpen(false);
        setSearchTerm("");
    }

    const hasMultipleItems = items.length > 1;
    const allIds = useMemo(() => items.map((item) => item.id), [items]);

    const isFullSelection = isCustomMode ? props.isFullSelection : selectedItems.length === allIds.length;

    const isPartiallySelected = isCustomMode
        ? props.isPartiallySelected
        : selectedItems.length > 0 && selectedItems.length < allIds.length;

    const isItemSelected = useCallback(
        (item: TItem) => {
            if (customIsItemSelected) {
                return customIsItemSelected(item);
            }

            return selectedItems.includes(item.id);
        },
        [customIsItemSelected, selectedItems],
    );

    const filteredItems = useMemo(() => {
        const term = deferredSearchTerm.trim().toLowerCase();

        if (!term) {
            return items;
        }

        const matches = filterItem ?? ((item: TItem, value: string) => (item.text ?? "").toLowerCase().includes(value));

        return items.filter((item) => matches(item, term));
    }, [items, deferredSearchTerm, filterItem]);

    const handleOpenChange = useCallback(
        (open: boolean) => {
            if (disabled && open) {
                return;
            }

            setIsOpen(open);

            if (!open) {
                setSearchTerm("");
            }
        },
        [disabled],
    );

    const handleSelectAll = useCallback(() => {
        if (customOnSelectAll) {
            customOnSelectAll();
            return;
        }

        if (!onSelectionChange) {
            return;
        }

        const isAllSelected = selectedItems.length === allIds.length;

        onSelectionChange(isAllSelected ? [] : [...allIds], true);
    }, [allIds, customOnSelectAll, onSelectionChange, selectedItems.length]);

    const handleSelect = useCallback(
        (item: TItem) => {
            if (customOnSelect) {
                customOnSelect(item);
                return;
            }

            if (!onSelectionChange) {
                return;
            }

            const isSelected = selectedItems.includes(item.id);
            const newSelected = isSelected ? selectedItems.filter((id) => id !== item.id) : [...selectedItems, item.id];

            onSelectionChange(newSelected, false);
        },
        [customOnSelect, onSelectionChange, selectedItems],
    );

    const trigger = renderButton ? (
        renderButton({ isOpen, disabled })
    ) : (
        <StyledBaseDropdownButtonWrap disabled={disabled}>
            <DropdownButton disabled={disabled}>{buttonText}</DropdownButton>
        </StyledBaseDropdownButtonWrap>
    );

    return (
        <ScrollDismissBoundary
            isOpen={isOpen}
            onClose={() => handleOpenChange(false)}
        >
            <Combobox
                isOpen={isOpen}
                onOpenChange={handleOpenChange}
                placement="bottom-start"
                width={width ? `${width}px` : undefined}
                data-automation={dataAutomation}
                {...(isTopLayer ? { "data-react-aria-top-layer": "true" } : {})}
            >
                <Combobox.Trigger>{trigger}</Combobox.Trigger>

                {hasSearch ? (
                    <Combobox.SearchInput
                        value={searchTerm}
                        onChange={setSearchTerm}
                        placeholder={searchPlaceHolder}
                    />
                ) : null}

                <Combobox.Content
                    emptyState={<Combobox.OptionLabel>{translate("dropdown.no.results")}</Combobox.OptionLabel>}
                >
                    {hasMultipleItems && !deferredSearchTerm ? (
                        <SelectAllOption
                            label={selectAllLabel}
                            isFullSelection={isFullSelection}
                            isPartiallySelected={isPartiallySelected}
                            onSelectAll={handleSelectAll}
                        />
                    ) : null}

                    {filteredItems.map((item) => (
                        <Combobox.Option
                            key={String(item.id)}
                            id={String(item.id)}
                            textValue={item.text ?? ""}
                            onClick={() => handleSelect(item)}
                        >
                            {renderOption(item, isItemSelected(item))}
                        </Combobox.Option>
                    ))}
                </Combobox.Content>
            </Combobox>
        </ScrollDismissBoundary>
    );
};

export default ComboboxWithSelectAll;
